import { Stack as ExpoStack, useLocalSearchParams } from 'expo-router';
import { Check, Edit3, Info, Plus, Send } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Keyboard,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { BackChevron, goBack } from '@/components/BackChevron';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { InsertTagSheet } from '@/components/InsertTagSheet';
import { MergeTagPill } from '@/components/MergeTagPill';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { renderTemplate, type MergeTag } from '@/lib/mergeTags';
import { formatRelative } from '@/lib/relativeTime';
import { remindersService } from '@/services/reminders';
import { smsSenderService } from '@/services/smsSender';
import { nudgeFeedStore } from '@/stores/everyoneFeed.store';
import { canStillFinish } from '@/lib/friendProgress';
import type { EveryoneFeedStage } from '@/services/referrals';
import { dealsStore } from '@/stores/deals.store';
import type { EveryoneFeedItem } from '@/types/referral';

/**
 * Used only when the deal has no authored `marketing_copy.nudge_template`.
 *
 * Deliberately names no product and no dollar amount. The previous default
 * hardcoded "my SoFi link" and "$50" — fine while SoFi Checking was the only
 * referable deal, and a lie the moment a second one existed (an Invest
 * referral would be pitched Checking's product and Checking's number). A bland
 * true message beats a specific false one; per-deal copy is authored in
 * `marketing_copy.nudge_template` (see deals:backfill_nudge_templates).
 */
const GENERIC_TEMPLATE =
  "Hey {{first_name}}! Saw you clicked my link but didn't finish. Takes 2 min — here's the link again: {{your_link}}";

const TOKEN_RE = /\{\{(\w+)\}\}/g;

/** A positive integer route param, or null — never NaN into a request. */
function parseId(raw: string | undefined): number | null {
  const id = Number.parseInt(raw ?? '', 10);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Nudge flow (v3, 2026-05-11). Replaces the old bulk Compose. Lets the
 * ambassador batch-text in-progress referrals from one screen:
 *
 *   1. Header — "Send a nudge" + how many people clicked but didn't finish
 *   2. Message card — template with merge-tag pills + Edit (deferred to a
 *      follow-up; for now the template is read-only)
 *   3. PICK WHO TO TEXT — Select-all toggle
 *   4. Scrollable checkbox list — in-progress feed items with phone_e164
 *   5. Text N people CTA — fires expo-sms with all selected phones
 *
 * Receives an optional `preselect_referral_id` param. When set, that
 * row is pre-selected and the rest start unselected (intent: tap NUDGE
 * on a single row in Who's-clicked, end up here ready to send to them).
 * Without the param, nothing is preselected and "Select all" is the
 * primary affordance.
 */
const NudgeScreen = observer(function NudgeScreen() {
  const insets = useSafeAreaInsets();
  const params = useLocalSearchParams<{ preselect_referral_id?: string; deal_id?: string; stage?: string }>();
  const dealId = parseId(params.deal_id);
  // Which friends to list. The deal screen's tab panels send their own tab
  // ("started": opened SoFi and stalled; "not_started": never did) so a
  // group text goes to the people it was written for. Anything else, and
  // a row-level nudge, lists everyone unpaid as before.
  const stage: EveryoneFeedStage =
    params.stage === 'started' || params.stage === 'not_started' ? params.stage : 'in_progress';
  // The deal being nudged, so the message names the right product. Null on
  // the invite screen's unscoped nudge, for a route that lost the param, or
  // before the deals list has landed (cold-start deep link) — all fall back
  // to the generic template until a deal resolves.
  const nudgeDeal = dealId != null ? dealsStore.deals.find((d) => d.id === dealId) ?? null : null;
  const dealTemplate = nudgeDeal?.marketing_copy?.nudge_template?.trim() || null;
  const preselectId = parseId(params.preselect_referral_id);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);
  // Editor state. `template` is the active body the user will send;
  // editing toggles between rendered (pills + literal) and raw TextInput.
  // Seeded from the deal's authored copy, else the brandless generic;
  // persists across edit toggles. Re-seeded if the deal's copy arrives
  // after first render (deals still loading), but never over the user's
  // own edits.
  const [template, setTemplate] = useState(dealTemplate ?? GENERIC_TEMPLATE);
  const [templateDirty, setTemplateDirty] = useState(false);
  const [editing, setEditing] = useState(false);
  const [insertOpen, setInsertOpen] = useState(false);

  useEffect(() => {
    if (templateDirty) return;
    setTemplate(dealTemplate ?? GENERIC_TEMPLATE);
  }, [dealTemplate, templateDirty]);

  const editTemplate = useCallback((next: string) => {
    setTemplateDirty(true);
    setTemplate(next);
  }, []);

  useEffect(() => {
    // Always fetch the in-progress set fresh on mount — coming in from
    // a row tap may have a stale list if the user paused on the Refer
    // tab while a refresh happened underneath them.
    // Scoped to the deal being nudged — otherwise the list mixes deals and
    // the single message body would be wrong for some of the recipients.
    // The store is reset first: it still holds the LAST nudge's rows, which
    // may belong to another deal, and rendering them under this deal's
    // message would text the wrong people the wrong link.
    nudgeFeedStore.reset();
    void nudgeFeedStore.fetch({ stage, dealId });
    void dealsStore.ensureLoaded();
  }, [dealId, stage]);

  // Nudgeable rows (in_progress + has phone, and not a dead action: the
  // Started tab lists rejected/expired friends in red, and "finish up" is
  // the wrong text for someone who can't), and only once THIS scope has
  // loaded — `loaded` is false from the reset above until the fetch lands,
  // so nothing from a previous scope can be selected or sent.
  const nudgeable = useMemo<EveryoneFeedItem[]>(
    () =>
      nudgeFeedStore.loaded
        ? nudgeFeedStore.items.filter(
          (i) => canStillFinish(i) && Boolean(i.phone_e164),
        )
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nudgeFeedStore.items, nudgeFeedStore.loaded],
  );

  // Apply preselect once when the feed lands. Don't keep re-applying on
  // every items change — that would override user toggles after fetch.
  const [appliedPreselect, setAppliedPreselect] = useState(false);
  useEffect(() => {
    if (appliedPreselect) return;
    if (!preselectId) {
      setAppliedPreselect(true);
      return;
    }
    if (nudgeable.length === 0) return;
    const match = nudgeable.find((i) => i.referral_id === preselectId);
    if (match) setSelected(new Set([match.referral_id]));
    setAppliedPreselect(true);
  }, [appliedPreselect, preselectId, nudgeable]);

  const toggle = useCallback((referralId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(referralId)) next.delete(referralId);
      else next.add(referralId);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(nudgeable.map((i) => i.referral_id)));
  }, [nudgeable]);

  const clearAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  const allSelected = selected.size > 0 && selected.size === nudgeable.length;
  const selectedCount = selected.size;
  const sendDisabled = selectedCount === 0 || sending || Platform.OS === 'web';

  const handleSend = useCallback(async () => {
    if (sendDisabled) return;
    setSending(true);
    try {
      const selectedItems = nudgeable
        .filter((i) => selected.has(i.referral_id))
        .filter((i): i is EveryoneFeedItem & { phone_e164: string } => Boolean(i.phone_e164));
      if (selectedItems.length === 0) {
        Alert.alert('No phone numbers', "None of the selected recipients have a phone we can text.");
        return;
      }
      // Open ONE iMessage compose per recipient, sequentially. Each
      // recipient sees only their own 1:1 thread (no stranger-to-
      // stranger group chat) and `{{first_name}}` resolves per person.
      // iOS Messages composer dismissal resolves the awaited promise,
      // so we naturally pause between recipients while the user is
      // still in Messages for the previous one.
      //
      // The share URL is resolved server-side (everyone_feed → share_url),
      // so the client just reads it. Null only if the ambassador has no
      // active link at all — skip that recipient and continue the batch.
      const skipped: string[] = [];
      for (const item of selectedItems) {
        if (!item.share_url) {
          skipped.push(item.first_name || item.name);
          continue;
        }
        const body = renderTemplate(template, {
          name: item.name,
          first_name: item.first_name,
          tracked_url: item.share_url,
        });
        const result = await smsSenderService.sendOne({
          phoneE164: item.phone_e164,
          body,
        });
        if (result.result === 'unavailable') {
          Alert.alert('Messages unavailable', 'iOS Messages is not available on this device.');
          return;
        }
        // Log the send. Not awaited and never rethrown: a logging failure
        // must not interrupt someone mid-batch, and the user's texts are the
        // point — the metric isn't.
        //
        // Logged on 'sent', 'cancelled' AND 'unknown'. iOS returns 'unknown'
        // almost every time (see smsSender's docstring), so branching any
        // finer would throw away nearly all the data. 'unavailable' is the
        // only outcome where no composer ever opened, and we've returned
        // above by then.
        void remindersService.logNudge(item.referral_id).catch(() => {
          // Swallowed — see above.
        });
      }
      if (skipped.length > 0) {
        Alert.alert(
          'Some texts skipped',
          `Couldn't build a share link for ${skipped.join(', ')}. Try them again in a minute.`,
        );
      }
      goBack('/referrals');
    } catch (err) {
      Alert.alert(
        'Could not open Messages',
        err instanceof Error ? err.message : 'Something went wrong.',
      );
    } finally {
      setSending(false);
    }
  }, [sendDisabled, nudgeable, selected, template]);

  const handleInsertTag = useCallback((token: MergeTag) => {
    setTemplateDirty(true);
    setTemplate((prev) => `${prev}{{${token}}}`);
    setInsertOpen(false);
  }, []);

  const openInsertTagSheet = useCallback(() => {
    Keyboard.dismiss();
    setInsertOpen(true);
  }, []);

  const isLoading = nudgeFeedStore.refreshing && !nudgeFeedStore.loaded;

  return (
    <SafeAreaView style={styles.safe} edges={['top', 'bottom']}>
      <ExpoStack.Screen options={{ headerShown: false }} />
      <View style={styles.header}>
        <BackChevron onPress={() => goBack('/referrals')} />
        <View style={styles.headerCopy}>
          <Text variant="bodyBold" color="deepGreen" style={styles.headerTitle}>
            Send a nudge
          </Text>
          <Text variant="caption" color="textMuted">
            {nudgeable.length} {nudgeable.length === 1 ? 'person' : 'people'} clicked but didn&apos;t finish
          </Text>
        </View>
      </View>

      <View style={styles.scrollWrap}>
        <Card variant="flat" padding={0} style={styles.messageCard}>
          <View style={styles.messageHeader}>
            <Text variant="bodyBold" color="deepGreen">
              Your message
            </Text>
            <Pressable
              onPress={() => setEditing((e) => !e)}
              hitSlop={6}
              accessibilityRole="button"
              accessibilityLabel={editing ? 'Done editing' : 'Edit message'}
            >
              <View style={styles.editChip}>
                {editing ? (
                  <Check size={12} color={colors.deepGreen} strokeWidth={2.5} />
                ) : (
                  <Edit3 size={12} color={colors.deepGreen} strokeWidth={2.25} />
                )}
                <Text variant="captionBold" color="deepGreen">
                  {editing ? 'Done' : 'Edit'}
                </Text>
              </View>
            </Pressable>
          </View>
          {editing ? (
            <>
              <TextInput
                value={template}
                onChangeText={editTemplate}
                multiline
                autoFocus
                accessibilityLabel="Message body"
                style={styles.editor}
              />
              <Pressable
                onPress={openInsertTagSheet}
                accessibilityRole="button"
                accessibilityLabel="Insert merge tag"
                style={({ pressed }) => [
                  styles.insertButton,
                  pressed && styles.insertButtonPressed,
                ]}
              >
                <Plus size={14} color={colors.deepGreen} strokeWidth={2.25} />
                <Text variant="captionBold" color="deepGreen">
                  Insert merge tag
                </Text>
              </Pressable>
            </>
          ) : (
            <RenderedTemplate template={template} />
          )}
          {/* Disclaimer hidden when 2+ recipients are selected — bulk
              iOS Messages group send can't per-recipient-template the
              first_name; all recipients would receive the FIRST one's
              first name. Showing "Each person gets their own first
              name filled in" in that case would be untrue. */}
          {selectedCount <= 1 ? (
            <View style={styles.disclaimer}>
              <Info size={12} color={colors.textMuted} strokeWidth={2.25} />
              <Text variant="caption" color="textMuted" style={styles.disclaimerText}>
                Each person gets their own first name filled in.
              </Text>
            </View>
          ) : null}
        </Card>

        <View style={styles.pickHeader}>
          <Text variant="captionBold" color="textMuted" style={styles.pickHeaderLabel}>
            PICK WHO TO TEXT
          </Text>
          {nudgeable.length > 0 ? (
            <Pressable
              onPress={allSelected ? clearAll : selectAll}
              accessibilityRole="button"
              accessibilityLabel={allSelected ? 'Clear selection' : `Select all ${nudgeable.length}`}
              hitSlop={6}
            >
              <Text variant="captionBold" color="deepGreen">
                {allSelected ? 'Clear all' : `Select all ${nudgeable.length}`}
              </Text>
            </Pressable>
          ) : null}
        </View>

        {isLoading ? (
          <View style={styles.loading}>
            <ActivityIndicator color={colors.deepGreen} />
          </View>
        ) : nudgeable.length === 0 ? (
          <Card variant="flat" style={styles.empty}>
            <Text variant="body" color="textMuted" align="center">
              Nobody is in progress right now. Share your link to start the funnel.
            </Text>
          </Card>
        ) : (
          <FlatList
            data={nudgeable}
            keyExtractor={(item) => `${item.referral_id}`}
            renderItem={({ item, index }) => (
              <RecipientRow
                item={item}
                selected={selected.has(item.referral_id)}
                isLast={index === nudgeable.length - 1}
                onToggle={() => toggle(item.referral_id)}
              />
            )}
            style={styles.list}
            contentContainerStyle={styles.listContent}
          />
        )}
      </View>

      <View style={[styles.footer, { paddingBottom: insets.bottom + spacing.base }]}>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          label={
            Platform.OS === 'web'
              ? 'iMessage is iOS only'
              : selectedCount > 0
                ? `Open iMessage for ${selectedCount} ${selectedCount === 1 ? 'person' : 'people'}`
                : 'Pick at least one person'
          }
          leadingIcon={Send}
          onPress={() => void handleSend()}
          disabled={sendDisabled}
          loading={sending}
        />
      </View>

      <InsertTagSheet
        isOpen={insertOpen}
        onClose={() => setInsertOpen(false)}
        onInsert={handleInsertTag}
      />
    </SafeAreaView>
  );
});

interface RenderedTemplateProps {
  template: string;
}

function RenderedTemplate({ template }: RenderedTemplateProps) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(TOKEN_RE.source, 'g');
  let key = 0;

  while ((match = re.exec(template)) !== null) {
    const literal = template.slice(lastIndex, match.index);
    if (literal) parts.push(<Text key={`l-${key++}`}>{literal}</Text>);
    const token = match[1] ?? '';
    parts.push(<MergeTagPill key={`p-${key++}`} token={token} value={token === 'first_name' ? 'first name' : token === 'your_link' ? 'your link' : token} />);
    lastIndex = re.lastIndex;
  }
  const tail = template.slice(lastIndex);
  if (tail) parts.push(<Text key={`t-${key++}`}>{tail}</Text>);

  return (
    <Text variant="body" color="text" style={styles.messageBody}>
      {parts}
    </Text>
  );
}

interface RecipientRowProps {
  item: EveryoneFeedItem;
  selected: boolean;
  isLast: boolean;
  onToggle: () => void;
}

function RecipientRow({ item, selected, isLast, onToggle }: RecipientRowProps) {
  return (
    <Pressable
      onPress={onToggle}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: selected }}
      accessibilityLabel={`${item.name} — ${selected ? 'selected' : 'not selected'}`}
      style={({ pressed }) => [
        styles.row,
        selected && styles.rowSelected,
        !isLast && styles.rowDivider,
        pressed && styles.rowPressed,
      ]}
    >
      <View style={[styles.checkbox, selected && styles.checkboxSelected]}>
        {selected ? (
          <Text style={styles.checkmark}>✓</Text>
        ) : null}
      </View>
      <View style={styles.avatar}>
        <Text style={styles.avatarText}>{initialsFor(item.name)}</Text>
      </View>
      <View style={styles.rowBody}>
        <Text variant="bodyBold" color="deepGreen" numberOfLines={1}>
          {item.name}
        </Text>
        <Text variant="caption" color="textMuted" numberOfLines={1}>
          {item.signed_up_at ? `Clicked ${formatRelative(item.signed_up_at)}` : 'Clicked recently'}
        </Text>
      </View>
    </Pressable>
  );
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

export default NudgeScreen;

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.md,
  },
  headerCopy: {
    flex: 1,
    gap: 2,
  },
  headerTitle: {
    fontSize: fontSizes.lg,
  },
  scrollWrap: {
    flex: 1,
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  messageCard: {
    padding: spacing.base,
    gap: spacing.sm,
  },
  messageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  editChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  messageBody: {
    lineHeight: 22,
  },
  editor: {
    minHeight: 96,
    maxHeight: 200,
    backgroundColor: colors.background,
    borderRadius: radii.base,
    borderWidth: 1,
    borderColor: colors.electricGreen,
    padding: spacing.sm,
    fontFamily: fonts.body,
    fontSize: fontSizes.base,
    lineHeight: 22,
    color: colors.text,
    textAlignVertical: 'top',
  },
  insertButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    borderRadius: radii.full,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  insertButtonPressed: {
    opacity: 0.7,
  },
  disclaimer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
  },
  disclaimerText: {
    flex: 1,
  },
  pickHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 4,
  },
  pickHeaderLabel: {
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  loading: {
    paddingVertical: spacing.xl,
    alignItems: 'center',
  },
  empty: {
    padding: spacing.lg,
  },
  list: {
    flex: 1,
    backgroundColor: colors.surface,
    borderRadius: radii.lg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  listContent: {
    paddingVertical: spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.sm,
  },
  rowDivider: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  rowSelected: {
    backgroundColor: colors.background,
  },
  rowPressed: {
    opacity: 0.7,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: '#B4B2A9',
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: colors.deepGreen,
    borderColor: colors.deepGreen,
  },
  checkmark: {
    color: colors.frost,
    fontSize: 12,
    fontFamily: fonts.bodyBold,
    lineHeight: 14,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: radii.full,
    backgroundColor: colors.deepGreen,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontFamily: fonts.bodyBold,
    fontSize: 11,
    color: colors.electricGreen,
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  footer: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    backgroundColor: colors.background,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
  },
});
